import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook for lazy loading images with IntersectionObserver
 * @param {string} src - Image source URL
 * @param {string} placeholder - Placeholder image URL (optional)
 * @param {Object} options - Observer options
 * @returns {Object} - { imageSrc, isLoaded, isInView, imageRef }
 */
const useLazyImage = (src, placeholder = '', options = {}) => {
  const [imageSrc, setImageSrc] = useState(placeholder);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imageRef = useRef();
  const observerRef = useRef();

  useEffect(() => {
    if (!src) return;

    // Default options for intersection observer
    const defaultOptions = {
      root: null,
      rootMargin: '50px', // Start loading when image is 50px away from viewport
      threshold: 0.01,
      ...options
    };

    // Create intersection observer
    observerRef.current = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        setImageSrc(src);
        // Disconnect observer after image enters viewport
        if (observerRef.current && imageRef.current) {
          observerRef.current.unobserve(imageRef.current);
        }
      }
    }, defaultOptions);

    // Start observing
    if (imageRef.current) {
      observerRef.current.observe(imageRef.current);
    }

    // Cleanup
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [src, options]);

  // Handle image load event
  const handleImageLoad = () => {
    setIsLoaded(true);
  };

  // Handle image error
  const handleImageError = () => {
    setIsLoaded(false);
    // Optionally set a fallback image
    if (placeholder && imageSrc !== placeholder) {
      setImageSrc(placeholder);
    }
  };

  return {
    imageSrc,
    isLoaded,
    isInView,
    imageRef,
    handleImageLoad,
    handleImageError
  };
};

export default useLazyImage;